import React from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight, Play, MapPin, ShieldCheck, BadgeCheck, Users, Phone, Mail } from "lucide-react";

const THUMBS = ["/pmr-photos/pmr-1.jpg", "/pmr-photos/pmr-3.jpg", "/pmr-photos/pmr-5.jpg", "/pmr-photos/pmr-9.jpg"];

export function HomeTopBar({ phone, email }) {
  return (
    <div className="bg-[#0b1637] text-white text-xs sm:text-[13px]" data-testid="home-top-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-between gap-6 overflow-x-auto whitespace-nowrap">
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5"><Heart size={13} className="text-amber-400" /> 80G Tax Benefit Available</span>
          <span className="hidden sm:inline w-px h-4 bg-white/20" />
          <span className="hidden sm:inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-amber-400" /> NGO Darpan: BR/2024/0396082</span>
        </div>
        <div className="flex items-center gap-6">
          <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 hover:text-amber-300 transition-colors" data-testid="top-bar-phone"><Phone size={13} /> {phone}</a>
          <a href={`mailto:${email}`} className="hidden md:inline-flex items-center gap-1.5 hover:text-amber-300 transition-colors" data-testid="top-bar-email"><Mail size={13} /> {email}</a>
        </div>
      </div>
    </div>
  );
}

function Doodles() {
  return (
    <>
      <svg className="absolute left-[2%] top-[10%] w-20 h-16 text-[#0b1637] hidden md:block hero-doodle" viewBox="0 0 80 60" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" aria-hidden>
        <path d="M4 24 L40 10 L76 24 L40 38 Z" /><path d="M18 30 V44 C30 52 50 52 62 44 V30" /><path d="M70 27 V44" /><circle cx="70" cy="47" r="2.5" />
      </svg>
      <svg className="absolute -left-2 top-[42%] w-14 h-14 text-orange-500 hidden md:block hero-doodle" style={{ animationDelay: "1.2s" }} viewBox="0 0 50 50" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden>
        <path d="M25 44 C8 32 3 22 7 14 C11 6 21 7 25 16 C29 7 40 6 43 14 C47 23 40 33 25 44 Z" />
      </svg>
      <div className="absolute right-0 top-2 hidden lg:block text-right rotate-[-12deg] font-hand text-[#0b1637] text-2xl leading-tight">
        Education<br /><span className="ml-2">Health</span><br /><span className="ml-6">Better Community</span>
        <svg viewBox="0 0 160 14" className="w-40 ml-auto text-orange-500" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden><path d="M4 11 C50 2 110 2 156 6" /></svg>
      </div>
    </>
  );
}

function LocationCard() {
  return (
    <div className="relative mt-2 lg:mt-0 lg:absolute lg:-bottom-6 lg:right-4 w-full lg:w-[440px] bg-[#0b1637] text-white rounded-2xl p-4 shadow-2xl shadow-[#0b1637]/30 home-rise" style={{ animationDelay: "0.5s" }} data-testid="hero-location-card">
      <div className="flex items-center gap-2 font-bold"><MapPin size={18} className="text-amber-400" /> Patna, Bihar, India
        <span className="ml-1 inline-flex flex-col w-6 h-4 rounded-sm overflow-hidden border border-white/30"><i className="flex-1 bg-[#FF9933]" /><i className="flex-1 bg-white" /><i className="flex-1 bg-[#138808]" /></span>
      </div>
      <p className="mt-1 text-sm text-slate-200">“Together for a stronger, brighter Bihar”</p>
      <div className="mt-3 grid grid-cols-4 gap-2">
        {THUMBS.map((t) => <img key={t} src={t} alt="PMR activity" className="h-14 w-full object-cover rounded-md border border-white/20 hover:scale-105 transition-transform" />)}
      </div>
    </div>
  );
}

const BADGES = [
  { icon: ShieldCheck, t: "80G Certified", s: "Tax Benefits Available" },
  { icon: BadgeCheck, t: "NGO Darpan Verified", s: "BR/2024/0396082" },
  { icon: Users, t: "Section 8 Company", s: "Legally Registered" },
];

export function HomeHero({ subtitle, videoUrl, onWatch }) {
  return (
    <section className="relative overflow-hidden bg-[#F7F9FD]" data-testid="home-hero">
      <div className="absolute -top-32 -left-32 w-[30rem] h-[30rem] rounded-full bg-blue-100/60 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-16 lg:pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
        <div className="home-rise">
          <div className="flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.25em] text-[#0b1637]"><span className="w-8 h-0.5 bg-orange-500" /> Bihar's Trusted NGO Platform</div>
          <h1 className="heading mt-5 text-5xl sm:text-6xl lg:text-[64px] xl:text-[70px] font-black leading-[0.98] tracking-tight text-[#0b1637]">
            Empowering<br />Lives,<br /><span className="text-orange-500 lg:whitespace-nowrap">Building Futures</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl" data-testid="hero-subtitle">
            {subtitle || "PMR Welfare Foundation is committed to uplifting communities through education, healthcare, and social support. Join 10,000+ donors who have already made a difference."}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/donate" className="group bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-8 py-4 inline-flex items-center gap-2.5 shadow-xl shadow-orange-500/30 transition-[background-color,transform] hover:-translate-y-0.5" data-testid="hero-donate-button">
              <Heart size={18} className="fill-white" /> Donate Now <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/programs" className="group border-2 border-[#0b1637] text-[#0b1637] hover:bg-[#0b1637] hover:text-white font-bold rounded-full px-8 py-[14px] inline-flex items-center gap-2.5 transition-colors" data-testid="hero-programs-button">
              Our Programs <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            {videoUrl && (
              <button onClick={onWatch} className="group inline-flex items-center gap-3 font-bold text-[#0b1637]" data-testid="hero-watch-video-button">
                <span className="w-12 h-12 rounded-full border-2 border-[#1E3A8A] flex items-center justify-center group-hover:bg-[#1E3A8A] group-hover:text-white transition-colors"><Play size={18} className="fill-current translate-x-0.5" /></span>
                Watch Story
              </button>
            )}
          </div>
          <div className="mt-10 flex flex-wrap gap-x-7 gap-y-4">
            {BADGES.map((b) => (
              <div key={b.t} className="flex items-center gap-3" data-testid={`hero-badge-${b.t.split(" ")[0].toLowerCase()}`}>
                <b.icon size={28} strokeWidth={1.6} className="text-[#1E3A8A]" />
                <div><div className="text-sm font-bold text-[#0b1637]">{b.t}</div><div className="text-xs text-slate-500">{b.s}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative lg:pb-10">
          <Doodles />
          <img src="/home/hero.jpg" alt="Children with PMR Welfare Foundation volunteer" className="relative w-full mix-blend-multiply home-rise" style={{ animationDelay: "0.2s" }} data-testid="hero-image" />
          <LocationCard />
        </div>
      </div>
    </section>
  );
}
